import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Shield, Crown, User, UserCog } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface UnifiedTeamsSectionProps {
  userTeams: any[];
}

export const UnifiedTeamsSection = ({ userTeams }: UnifiedTeamsSectionProps) => {
  const navigate = useNavigate();

  if (userTeams.length === 0) {
    return null;
  }

  const handleTeamClick = (team: any) => {
    console.log('Clicking team:', team);
    if (team.slug) {
      console.log('Navigating to team profile:', `/team/${team.slug}`);
      navigate(`/team/${team.slug}`);
    } else {
      console.error('Team has no slug:', team);
    }
  };

  const getRoleIcon = (userRole: string) => {
    switch (userRole) {
      case 'owner':
        return <Crown className="h-4 w-4 text-yellow-400" />;
      case 'coach':
        return <Shield className="h-4 w-4 text-blue-400" />;
      case 'player':
        return <User className="h-4 w-4 text-green-400" />;
      case 'support':
        return <UserCog className="h-4 w-4 text-purple-400" />;
      default:
        return <Users className="h-4 w-4 text-gray-400" />;
    }
  };

  const getRoleLabel = (team: any) => {
    switch (team.user_role) {
      case 'owner':
        return 'Owner';
      case 'coach':
        return team.coaching_role 
          ? team.coaching_role.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
          : 'Coach';
      case 'player':
        return team.position ? `Player (${team.position})` : 'Player';
      case 'support':
        return 'Support Staff';
      default:
        return 'Member';
    }
  };

  const getRoleBadgeColor = (userRole: string) => {
    switch (userRole) {
      case 'owner':
        return 'bg-yellow-600/20 text-yellow-300 border-yellow-600/30';
      case 'coach':
        return 'bg-blue-600/20 text-blue-300 border-blue-600/30';
      case 'player':
        return 'bg-green-600/20 text-green-300 border-green-600/30';
      case 'support':
        return 'bg-purple-600/20 text-purple-300 border-purple-600/30';
      default:
        return 'bg-gray-600/20 text-gray-300 border-gray-600/30';
    }
  };

  const formatMemberCount = (count: number) => {
    if (count === 0) return 'No members';
    if (count === 1) return '1 member';
    return `${count} members`;
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="text-foreground flex items-center gap-2">
          <Users className="h-5 w-5" />
          Teams ({userTeams.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {userTeams.map((team, index) => (
            <div 
              key={team.team_id || index} 
              className="bg-muted/50 p-4 rounded-lg hover:bg-muted/80 transition-colors cursor-pointer border border-border"
              onClick={() => handleTeamClick(team)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  {team.team_logo ? (
                    <img 
                      src={team.team_logo} 
                      alt={team.team_name}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="text-foreground font-semibold text-base mb-1 truncate">
                      {team.team_name}
                    </h4>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${getRoleBadgeColor(team.user_role)}`}
                      >
                        <span className="flex items-center gap-1">
                          {getRoleIcon(team.user_role)}
                          {getRoleLabel(team)}
                        </span>
                      </Badge>
                    </div>
                    
                    {team.sport_type && (
                      <p className="text-muted-foreground text-sm mb-1">
                        {team.sport_type}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        {team.member_count !== undefined 
                          ? formatMemberCount(team.member_count)
                          : 'Team members'
                        }
                      </span>
                      {team.slug && (
                        <span className="font-mono">/{team.slug}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};